
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { User, DocumentType, Document } from '../types';
import { api } from '../services/api';
import { isFinancialDocument } from '../utils/financialUtils';
import {
    FileText,
    Upload,
    Trash2,
    CheckCircle,
    XCircle,
    Clock,
    Loader2,
    File as FileIcon,
    Calendar,
    Search,
    Filter,
    X as CloseIcon,
    Mail,
    Inbox,
    MessageSquare
} from 'lucide-react';
import DocumentActionModal from './DocumentActionModal';
import { DocumentNotesModal } from './DocumentNotesModal';
import { LocalizedDatePicker } from './ui/LocalizedDatePicker';

import { formatCurrency, formatDate } from '../utils/formatters';

interface ClientDocumentsViewProps {
    user: User;
    agencyUser?: User;
}

export const ClientDocumentsView: React.FC<ClientDocumentsViewProps> = ({ user, agencyUser }) => {
    const { t } = useTranslation('documents');
    const [docs, setDocs] = useState<Document[]>([]);
    const [types, setTypes] = useState<DocumentType[]>([]);
    const [agencyName, setAgencyName] = useState<string>('');
    const [agencyConfig, setAgencyConfig] = useState<any>(null); // Store agency config
    const [isLoading, setIsLoading] = useState(true);
    const [isUploading, setIsUploading] = useState(false);
    const [activeTab, setActiveTab] = useState<'history' | 'upload'>('history');



    // Filter State
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('');
    const [filterStatus, setFilterStatus] = useState('');

    const [filterDateFrom, setFilterDateFrom] = useState('');
    const [filterDateTo, setFilterDateTo] = useState('');

    // Modal State
    const [modalConfig, setModalConfig] = useState<{
        isOpen: boolean;
        type: 'success' | 'error' | 'delete';
        title: string;
        message: string;
        docId?: string;
        docTitle?: string;
        docType?: string;
        docTypeId?: string;
        showNotifyBtn?: boolean;
    }>({
        isOpen: false,
        type: 'success',
        title: '',
        message: '',
        docId: '',
        docTitle: '',
        docType: '',
        showNotifyBtn: false
    });
    const [isDeleting, setIsDeleting] = useState(false);
    const [isSendingEmail, setIsSendingEmail] = useState(false);

    // Notes State
    const [showNotesModal, setShowNotesModal] = useState(false);
    const [notesDoc, setNotesDoc] = useState<Document | null>(null);

    const handleNotesClick = (doc: Document) => {
        setNotesDoc(doc);
        setShowNotesModal(true);

        // Mark as read for Client
        if (doc.metadata?.hasUnreadNotesForClient) {
            api.markDocumentNotesAsRead(doc.id, false).then(() => {
                // Optimistic update
                setDocs(prev => prev.map(d => d.id === doc.id ? {
                    ...d,
                    metadata: { ...d.metadata, hasUnreadNotesForClient: false }
                } : d));
            });
        }
    };

    // Upload Form State
    const [selectedTypeId, setSelectedTypeId] = useState('');
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [uploadLabel, setUploadLabel] = useState(''); // Kept just in case, but unused for bulk
    const [periodStart, setPeriodStart] = useState('');
    const [periodEnd, setPeriodEnd] = useState('');

    const newDocsCount = docs.filter(d => d.uploadedByRole === 'agency' && !d.clientViewed).length;

    const handleMarkAsViewed = async () => {
        // Optimistic update
        const unseenIds = docs.filter(d => d.uploadedByRole === 'agency' && !d.clientViewed).map(d => d.id);
        if (unseenIds.length === 0) return;

        setDocs(prev => prev.map(d => unseenIds.includes(d.id) ? { ...d, clientViewed: true } : d));

        // Background update
        unseenIds.forEach(id => {
            api.updateDocument(id, { clientViewed: true }).catch(console.error);
        });
    };

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setIsLoading(true);
        try {
            // Robust check: use user.agencyId OR agencyUser.id (if acting)
            const targetAgencyId = user.agencyId || agencyUser?.id;

            if (targetAgencyId) {
                // 1. Fetch Docs and Types (Critical)
                const [myDocs, myTypes] = await Promise.all([
                    api.getDocuments(targetAgencyId, user.id),
                    api.getDocumentTypes(targetAgencyId, true) // true = client view
                ]);
                setDocs(myDocs);
                setTypes(myTypes);

                // 2. Fetch Agency Name & Config
                try {
                    const agencyInfo = await api.getUser(targetAgencyId);
                    setAgencyName(agencyInfo.profile?.companyName || agencyInfo.config?.companyName || 'Agency');
                    setAgencyConfig(agencyInfo.config);
                } catch (e) {
                    setAgencyName('Agency');
                }
            }
        } catch (error) {
            console.error("Error loading docs:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            setSelectedFiles(Array.from(e.target.files));
        }
    };

    const handleUpload = async (e: React.FormEvent) => {
        e.preventDefault();
        if (selectedFiles.length === 0 || !user.agencyId) return;

        const currentTypeId = selectedTypeId;
        console.log('[DEBUG] Starting Bulk Upload with TypeID:', currentTypeId);

        setIsUploading(true);
        let successCount = 0;
        let failCount = 0;

        try {
            // Sanitize function
            const sanitize = (str: string) => {
                const greekMap: { [key: string]: string } = {
                    'Α': 'A', 'Β': 'V', 'Γ': 'G', 'Δ': 'D', 'Ε': 'E', 'Ζ': 'Z', 'Η': 'I', 'Θ': 'TH',
                    'Ι': 'I', 'Κ': 'K', 'Λ': 'L', 'Μ': 'M', 'Ν': 'N', 'Ξ': 'X', 'Ο': 'O', 'Π': 'P',
                    'Ρ': 'R', 'Σ': 'S', 'Τ': 'T', 'Υ': 'Y', 'Φ': 'F', 'Χ': 'CH', 'Ψ': 'PS', 'Ω': 'O',
                    'α': 'a', 'β': 'v', 'γ': 'g', 'δ': 'd', 'ε': 'e', 'ζ': 'z', 'η': 'i', 'θ': 'th',
                    'ι': 'i', 'κ': 'k', 'λ': 'l', 'μ': 'm', 'ν': 'n', 'ξ': 'x', 'ο': 'o', 'π': 'p',
                    'ρ': 'r', 'σ': 's', 'τ': 't', 'υ': 'y', 'φ': 'f', 'χ': 'ch', 'ψ': 'ps', 'ω': 'o',
                    'ς': 's', 'Ά': 'A', 'Έ': 'E', 'Ή': 'I', 'Ί': 'I', 'Ό': 'O', 'Ύ': 'Y', 'Ώ': 'O',
                    'ά': 'a', 'έ': 'e', 'ή': 'i', 'ί': 'i', 'ό': 'o', 'ύ': 'y', 'ώ': 'o', 'ϊ': 'i',
                    'ϋ': 'y', 'ΐ': 'i', 'ΰ': 'y'
                };
                const transliterated = str.split('').map(char => greekMap[char] || char).join('');
                return transliterated.replace(/[^a-zA-Z0-9-_]/g, '_');
            };

            const agencyFolder = user.agencyId;
            const clientFolder = user.id;
            const typeItem = types.find(t => t.id === currentTypeId);
            const typeFolder = sanitize(typeItem?.name || 'Other');
            const year = new Date().getFullYear();

            // Loop through all selected files
            for (const file of selectedFiles) {
                try {
                    const timestamp = Date.now();
                    const filename = `${timestamp}_${sanitize(file.name)}`;
                    // Use 'inbox' as staging area for client uploads
                    const path = `inbox/${agencyFolder}/${clientFolder}/${year}/${typeFolder}/${filename}`;

                    const metadata = {
                        agencyId: user.agencyId,
                        clientId: user.id
                    };

                    const downloadUrl = await api.uploadDocumentFile(file, path, metadata);

                    await api.createDocument({
                        typeId: currentTypeId,
                        typeName: typeItem?.name || 'Unknown',
                        title: file.name, // Use original filename as title
                        agencyId: user.agencyId,
                        clientId: user.id,
                        uploadedBy: user.id,
                        uploadedByRole: 'client',
                        fileUrl: downloadUrl,
                        storagePath: path,
                        fileType: file.type,
                        size: file.size,
                        status: 'pending',
                        version: 1,
                        metadata: {},
                        ai_analysis: null // Explicitly Null to skip/indicate no OCR
                    });
                    successCount++;
                } catch (err) {
                    console.error("Failed to upload file:", file.name, err);
                    failCount++;
                }
            }

            // Reset
            setSelectedFiles([]);
            setUploadLabel('');
            setPeriodStart('');
            setPeriodEnd('');
            setSelectedTypeId('');
            loadData();
            setActiveTab('history');

            setModalConfig({
                isOpen: true,
                type: successCount > 0 ? 'success' : 'error',
                title: successCount > 0 ? t('messages.upload_success') : t('messages.upload_failed'),
                message: `${successCount} files uploaded successfully.${failCount > 0 ? ` ${failCount} failed.` : ''}`,
                showNotifyBtn: !!agencyUser
            });

        } catch (error: any) {
            console.error(error);
            setModalConfig({
                isOpen: true,
                type: 'error',
                title: t('messages.upload_failed'),
                message: error.message || t('messages.upload_failed_generic')
            });
        } finally {
            setIsUploading(false);
        }
    };



    const handleDocClick = (doc: Document) => {
        if (doc.uploadedByRole === 'agency' && !doc.clientViewed) {
            // Optimistic Update
            setDocs(prev => prev.map(d => d.id === doc.id ? { ...d, clientViewed: true } : d));
            // Background Update
            api.updateDocument(doc.id, { clientViewed: true }).catch(console.error);
        }
    };

    const handleDeleteClick = (doc: Document) => {
        if (doc.status !== 'pending') return;
        setModalConfig({
            isOpen: true,
            type: 'delete',
            title: t('messages.delete_title'),
            message: t('messages.delete_confirm', { title: doc.title }) + ' ' + t('messages.delete_desc'),
            docId: doc.id
        });
    };

    const confirmDelete = async () => {
        if (!modalConfig.docId) return;
        setIsDeleting(true);
        try {
            // Find document to get storagePath
            const doc = docs.find(d => d.id === modalConfig.docId);
            await api.deleteDocument(modalConfig.docId, doc?.storagePath);

            loadData();
            setModalConfig(prev => ({ ...prev, isOpen: false }));
        } catch (e) {
            setModalConfig({
                isOpen: true,
                type: 'error',
                title: 'Delete Failed',
                message: 'Could not delete the document. Please try again.'
            });
        } finally {
            setIsDeleting(false);
        }
    };

    const handleSendNotification = async () => {
        if (!modalConfig.docId) return;
        setIsSendingEmail(true);
        try {
            await api.sendDocumentNotification({
                clientId: user.id,
                docTitle: modalConfig.docTitle || 'Document',
                docType: modalConfig.docType || 'Document',
                docTypeId: modalConfig.docTypeId,
                agencyName: agencyName
            });
            setModalConfig(prev => ({ ...prev, isOpen: false }));
            // Optional: Show a quick toast or alert? relying on closing as success.
            // Using alert strictly for feedback that action completed as it's separate from modal context now.
            // Actually, let's just assume closing is fine or user sees it. But user wants confirmation.
            // But we can't switch modal content easily without more state. 
            // Let's just close it.
        } catch (e) {
            alert('Failed to send notification email.');
        } finally {
            setIsSendingEmail(false);
        }
    };

    // Sorting State
    const [sortConfig, setSortConfig] = useState<{ key: 'date' | 'type' | 'status'; direction: 'asc' | 'desc' }>({ key: 'date', direction: 'desc' });

    const requestSort = (key: 'date' | 'type' | 'status') => {
        let direction: 'asc' | 'desc' = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (key: 'date' | 'type' | 'status') => {
        if (sortConfig.key !== key) return <span className="opacity-0">↕</span>;
        return sortConfig.direction === 'asc' ? '↑' : '↓';
    };

    const filteredDocs = docs.filter(doc => {
        // 1. Search Title
        if (searchTerm && !doc.title.toLowerCase().includes(searchTerm.toLowerCase())) return false;

        // 2. Type
        if (filterType && doc.typeId !== filterType) return false;

        // 3. Status
        if (filterStatus && doc.status !== filterStatus) return false;

        // 4. Referring Period Date
        // 4. Referring Period Date
        if (filterDateFrom || filterDateTo) {
            const docStart = doc.metadata?.periodStart;
            const docEnd = doc.metadata?.periodEnd;

            // If document has no period, it shouldn't match a period filter
            if (!docStart && !docEnd) return false;

            // Filter logic: Document Period must overlap or be contained?
            // Usually "Show docs for period X" means docs where periodStart >= X and periodEnd <= Y (Strict containment)
            // or docs that OVERLAP.
            // Let's use strict containment for "Period" filtering as usually users want specific months.

            if (filterDateFrom && docStart && docStart < filterDateFrom) return false;
            if (filterDateTo && docEnd && docEnd > filterDateTo) return false;

            // If we only have start, check if doc starts after it
            if (filterDateFrom && !docStart) return false; // Can't validly compare
            // If we only have end, check if doc ends before it
            if (filterDateTo && !docEnd) return false;
        }

        return true;
    }).sort((a, b) => {
        let comparison = 0;
        switch (sortConfig.key) {
            case 'date':
                comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
                break;
            case 'type':
                comparison = a.typeName.localeCompare(b.typeName);
                break;
            case 'status':
                comparison = a.status.localeCompare(b.status);
                break;
        }
        return sortConfig.direction === 'asc' ? comparison : -comparison;
    });

    return (
        <div className="space-y-8 mx-auto w-full">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold font-display text-slate-900 flex items-center gap-2">
                    <FileText size={28} className="text-blue-600" /> {t('title.client_view')}
                </h1>

                <div className="flex bg-slate-100 p-1 rounded-xl">
                    <button
                        onClick={() => setActiveTab('history')}
                        className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${activeTab === 'history' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                        {t('tabs.history')}
                    </button>
                    <button
                        onClick={() => setActiveTab('upload')}
                        className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${activeTab === 'upload' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                        {t('tabs.upload')}
                    </button>
                </div>
            </div>

            {/* Upload Section */}
            {activeTab === 'upload' && (
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8">
                    <h2 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
                        <Upload size={20} className="text-slate-500" /> {t('upload.title_plural') || 'Upload Documents'}
                    </h2>

                    <form onSubmit={handleUpload} className="grid grid-cols-1 md:grid-cols-2 gap-6">

                        <div className="md:col-span-2">
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-2">{t('upload.doc_type')}</label>
                            <select
                                required
                                value={selectedTypeId}
                                onChange={e => setSelectedTypeId(e.target.value)}
                                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-sm outline-none focus:ring-2 focus:ring-black"
                            >
                                <option value="">{t('upload.choose_type')}</option>
                                {types.filter(t => t.clientCanUpload).map(tOption => (
                                    <option key={tOption.id} value={tOption.id}>
                                        {tOption.nameTranslations?.[user.language as 'en' | 'el'] || tOption.nameTranslations?.en || tOption.name}
                                    </option>
                                ))}
                            </select>
                            <p className="text-xs text-slate-400 mt-1">
                                Please select the type of documents you are uploading (e.g., Invoices, Bank Statements).
                            </p>
                        </div>

                        <div className="md:col-span-2">
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-2">{t('upload.files')}</label>
                            <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 flex flex-col items-center justify-center gap-2 hover:border-black transition cursor-pointer relative bg-slate-50">
                                <input
                                    type="file"
                                    multiple
                                    required
                                    onChange={handleFileSelect}
                                    className="absolute inset-0 opacity-0 cursor-pointer"
                                />
                                {selectedFiles.length > 0 ? (
                                    <div className="flex flex-col items-center">
                                        <FileIcon size={32} className="text-blue-600 mb-2" />
                                        <span className="font-bold text-slate-900">{selectedFiles.length} files selected</span>
                                        <div className="text-xs text-slate-500 mt-1 max-w-sm text-center">
                                            {selectedFiles.map(f => f.name).slice(0, 3).join(', ')}
                                            {selectedFiles.length > 3 && `... and ${selectedFiles.length - 3} more`}
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <Upload size={32} className="text-slate-400" />
                                        <span className="font-medium text-slate-600">{t('upload.drag_drop_multiple')}</span>
                                        <span className="text-xs text-slate-400">{t('upload.supported_formats')}</span>
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="md:col-span-2 flex justify-end">
                            <button
                                type="submit"
                                disabled={selectedFiles.length === 0 || !selectedTypeId || isUploading}
                                className="bg-black text-white px-8 py-3 rounded-xl font-bold hover:opacity-80 transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isUploading && <Loader2 className="animate-spin" size={18} />}
                                {isUploading ? t('common.uploading') : t('common.upload_bulk')}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Dashboard Stats Widget for New Documents */}
            {newDocsCount > 0 && activeTab === 'history' && (
                <div className="mb-6 bg-white p-6 rounded-2xl border border-blue-100 shadow-sm flex items-center justify-between bg-gradient-to-r from-blue-50 to-white">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center relative">
                            <span className="absolute top-2 right-2 w-3 h-3 bg-red-500 rounded-full border-2 border-white animate-pulse"></span>
                            <Inbox size={24} />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-slate-900">{t('common.new_docs')} {agencyName}</h3>
                            <p className="text-sm text-slate-500">{newDocsCount !== 1 ? t('messages.new_docs_alert_plural', { count: newDocsCount }) : t('messages.new_docs_alert', { count: newDocsCount })}</p>
                        </div>
                    </div>
                    <button
                        onClick={handleMarkAsViewed}
                        className="bg-blue-600 text-white px-5 py-2.5 rounded-xl font-bold hover:bg-blue-700 transition shadow-lg flex items-center gap-2"
                    >
                        <CheckCircle size={18} />
                        {t('actions.mark_seen')}
                    </button>
                </div>
            )}

            {/* Documents List */}
            {activeTab === 'history' && (
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="p-6 border-b border-slate-100 bg-slate-50 flex flex-col gap-4">
                        <div className="flex justify-between items-center">
                            <h2 className="text-lg font-bold text-slate-900">{t('tabs.history')}</h2>
                            {(searchTerm || filterType || filterStatus || filterDateFrom || filterDateTo) && (
                                <button
                                    onClick={() => {
                                        setSearchTerm('');
                                        setFilterType('');
                                        setFilterStatus('');
                                        setFilterType('');
                                        setFilterStatus('');
                                        setFilterDateFrom('');
                                        setFilterDateTo('');
                                    }}
                                    className="text-xs font-bold text-red-500 flex items-center gap-1 hover:underline"
                                >
                                    <CloseIcon size={12} /> {t('filter.clear')}
                                </button>
                            )}
                        </div>

                        {/* Filters Bar */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div className="relative">
                                <Search size={16} className="absolute left-3 top-3 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder={t('filter.search_placeholder')}
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-black"
                                />
                            </div>

                            <select
                                value={filterType}
                                onChange={e => setFilterType(e.target.value)}
                                className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-black"
                            >
                                <option value="">{t('filter.all_types')}</option>
                                {types.map(tOption => (
                                    <option key={tOption.id} value={tOption.id}>
                                        {tOption.nameTranslations?.[user.language as 'en' | 'el'] || tOption.nameTranslations?.en || tOption.name}
                                    </option>
                                ))}
                            </select>

                            <select
                                value={filterStatus}
                                onChange={e => setFilterStatus(e.target.value)}
                                className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-black"
                            >
                                <option value="">{t('filter.all_statuses')}</option>
                                <option value="pending">{t('status.pending')}</option>
                                <option value="approved">{t('status.approved')}</option>
                                <option value="rejected">{t('status.rejected')}</option>
                            </select>

                            <div className="flex gap-2">
                                <div className="w-1/2">
                                    <LocalizedDatePicker
                                        value={filterDateFrom}
                                        onChange={setFilterDateFrom}
                                        format={user.dateFormat}
                                        placeholder={t('filter.date_from') || 'From'}
                                        className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-black"
                                    />
                                </div>
                                <div className="w-1/2">
                                    <LocalizedDatePicker
                                        value={filterDateTo}
                                        onChange={setFilterDateTo}
                                        format={user.dateFormat}
                                        placeholder={t('filter.date_to') || 'To'}
                                        className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-black"
                                    />
                                </div>
                            </div>
                        </div>
                        {/* One more fix: The DateInput takes up height due to label. 
                            Let's just put "Filter by Period" as label to look nice.
                        */}
                    </div>

                    {isLoading ? (
                        <div className="p-12 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-sm">
                                <thead className="bg-slate-50 text-slate-500">
                                    <tr>
                                        <th className="px-6 py-4">{t('table.title')}</th>
                                        <th
                                            className="px-6 py-4 cursor-pointer hover:bg-slate-100 select-none"
                                            onClick={() => requestSort('type')}
                                        >
                                            {t('table.type')} {getSortIcon('type')}
                                        </th>
                                        <th
                                            className="px-6 py-4 cursor-pointer hover:bg-slate-100 select-none"
                                            onClick={() => requestSort('date')}
                                        >
                                            {t('table.upload_date')} {getSortIcon('date')}
                                        </th>
                                        <th className="px-6 py-4">{t('table.referring_period')}</th>
                                        <th
                                            className="px-6 py-4 cursor-pointer hover:bg-slate-100 select-none"
                                            onClick={() => requestSort('status')}
                                        >
                                            {t('table.status')} {getSortIcon('status')}
                                        </th>

                                        <th className="px-6 py-4 text-center">{t('table.payment_status')}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {filteredDocs.length === 0 ? (
                                        <tr><td colSpan={6} className="px-6 py-12 text-center text-slate-400">
                                            {t('messages.no_docs')}
                                        </td></tr>
                                    ) : (
                                        filteredDocs.map(doc => (
                                            <tr key={doc.id} className="hover:bg-slate-50">
                                                <td className="px-6 py-4 font-bold text-slate-900 max-w-xs">
                                                    <div className="flex items-center gap-2">
                                                        {agencyConfig?.enableDocumentNotes !== false && (
                                                            <button
                                                                onClick={() => handleNotesClick(doc)}
                                                                className="text-slate-300 hover:text-yellow-500 transition relative group"
                                                                title="Notes"
                                                            >
                                                                <MessageSquare
                                                                    size={18}
                                                                    className={
                                                                        doc.metadata?.hasUnreadNotesForClient
                                                                            ? "text-red-500 fill-red-500 animate-pulse"
                                                                            : doc.metadata?.notes
                                                                                ? "text-yellow-500 fill-yellow-500"
                                                                                : ""
                                                                    }
                                                                />
                                                            </button>
                                                        )}
                                                        <div className="truncate" title={doc.title}>
                                                            <a
                                                                href={doc.fileUrl}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                onClick={() => handleDocClick(doc)}
                                                                className="flex items-center gap-2 hover:underline group"
                                                            >
                                                                <FileText size={16} className="text-blue-500 shrink-0" />
                                                                <span className="truncate">{doc.title}</span>
                                                                {doc.uploadedByRole === 'agency' && !doc.clientViewed && (
                                                                    <span className="ml-2 bg-blue-600 text-white text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider animate-pulse shrink-0">New</span>
                                                                )}
                                                            </a>
                                                        </div>
                                                    </div>
                                                </td>

                                                <td className="px-6 py-4 text-slate-500">
                                                    {(() => {
                                                        const docType = types.find(t => t.id === doc.typeId || t.name === doc.typeName);
                                                        // Fallback to cached doc.typeName if type not found, or translate it if possible? 
                                                        // Ideal: use docType.nameTranslations. If not found, show doc.typeName.
                                                        if (docType) {
                                                            return docType.nameTranslations?.[user.language as 'en' | 'el'] || docType.nameTranslations?.en || docType.name;
                                                        }
                                                        return doc.typeName;
                                                    })()}
                                                </td>
                                                <td className="px-6 py-4 text-slate-500">{formatDate(doc.createdAt, user.dateFormat)}</td>
                                                <td className="px-6 py-4 text-slate-500 text-xs text-nowrap">
                                                    {doc.metadata?.periodStart ? (
                                                        <span className="font-mono bg-slate-100 px-1 py-0.5 rounded">
                                                            {formatDate(doc.metadata.periodStart, user.dateFormat)} to {formatDate(doc.metadata.periodEnd || '', user.dateFormat)}
                                                        </span>
                                                    ) : '-'}
                                                </td>
                                                <td className="px-6 py-4">
                                                    {doc.status === 'pending' && <span className="flex items-center gap-1 text-amber-600 bg-amber-50 px-2 py-1 rounded-full text-xs font-bold"><Clock size={12} /> {t('status.pending')}</span>}
                                                    {doc.status === 'approved' && <span className="flex items-center gap-1 text-green-600 bg-green-50 px-2 py-1 rounded-full text-xs font-bold"><CheckCircle size={12} /> {t('status.approved')}</span>}
                                                    {doc.status === 'rejected' && <span className="flex items-center gap-1 text-red-600 bg-red-50 px-2 py-1 rounded-full text-xs font-bold"><XCircle size={12} /> {t('status.rejected')}</span>}
                                                </td>

                                                <td className="px-6 py-4 text-center">
                                                    {isFinancialDocument(doc, types) ? (
                                                        doc.paymentStatus === 'paid' ? (
                                                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                <CheckCircle size={14} />
                                                                {t('status.paid')}
                                                            </span>
                                                        ) : (
                                                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                                                <Clock size={14} />
                                                                {t('status.pending')}
                                                            </span>
                                                        )
                                                    ) : (
                                                        <span className="text-slate-300">-</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            <DocumentActionModal
                isOpen={modalConfig.isOpen}
                type={modalConfig.type}
                title={modalConfig.title}
                message={modalConfig.message}
                onClose={() => setModalConfig(prev => ({ ...prev, isOpen: false }))}
                onConfirm={confirmDelete}
                onNotify={handleSendNotification}
                isLoading={isDeleting || isSendingEmail}
                showNotifyBtn={modalConfig.showNotifyBtn}
            />

            {notesDoc && (
                <DocumentNotesModal
                    isOpen={showNotesModal}
                    onClose={() => { setShowNotesModal(false); setNotesDoc(null); }}
                    documentId={notesDoc.id}
                    documentTitle={notesDoc.title}
                    currentUser={user}
                    agencyName={agencyName}
                />
            )}
        </div>
    );
};


